import { trackInternalEvent } from '@rio/tracking'
import { Resume } from 'packages/types'
import { useI18n } from 'builder/hooks/useI18n'
import { TooltipV2 } from 'builder/components/Tooltip'
import {
  FontChangeHandler,
  SizeChangeHandler,
  SpacingChangeHandler,
  SettingsFieldChangeHandler,
} from 'builder/modules/resumeEditor'
import {
  SelectContainer,
  SelectLabel,
  CustomSelect,
  SliderContainer,
  SliderLabel,
  TabContentContainer,
  GroupControl,
} from '../../styles'
import { useTemplateCustomization } from '../../hooks/useTemplateCustomization'
import { useTextCustomization } from '../../hooks/useTextCustomization'
import { SpacingSlider } from '../CustomizationSlider/SpacingSlider'
import { SizeSlider } from '../CustomizationSlider/SizeSlider'
import { FontType } from '../../constants'
import { FontContainer } from './styles'
import { FontSizesCustomization } from './FontSizesCustomization'
import { FontWeightsCustomization } from './FontWeightCustomization'

type Props = {
  resume: Resume
  handleFontChange?: FontChangeHandler
  handleSpacingChange?: SpacingChangeHandler
  handleSizeChange?: SizeChangeHandler
  handleSettingsFieldChange?: SettingsFieldChangeHandler
}

const TRANSLATION = 'builder.resume_editor.document_customization.sidebar.text_tab'

export const TextCustomization = (props: Props) => {
  const { resume, handleFontChange, handleSizeChange, handleSpacingChange } = props
  const { i18n } = useI18n()
  const { templateName, currentTemplate } = useTemplateCustomization(resume)

  const {
    primaryFonts,
    secondaryFonts,
    tertiaryFonts,
    isPrimaryFontCustomizationEnabled,
    isSecondaryFontCustomizationEnabled,
    isTertiaryFontCustomizationEnabled,
    selectedPrimaryFont,
    selectedSecondaryFont,
    selectedTertiaryFont,
  } = useTextCustomization(resume)

  const handleFontSelectionClick = (fontType: FontType): void => {
    trackInternalEvent('click_resume_font_menu', { label: fontType, template: templateName })
  }

  return (
    <TabContentContainer>
      <FontContainer>
        {primaryFonts?.length > 0 && (
          <TooltipV2
            value={i18n.t(`${TRANSLATION}.font_customization_warning`)}
            width={210}
            multiline={true}
            position="bottom"
            isActive={!isPrimaryFontCustomizationEnabled}
          >
            <SelectContainer>
              <SelectLabel>
                {i18n.t(`${TRANSLATION}.${secondaryFonts?.length > 0 ? 'primary_font' : 'font'}`)}
              </SelectLabel>
              <CustomSelect
                selected={selectedPrimaryFont}
                onSelect={value =>
                  handleFontChange?.({
                    value,
                    newFontType: 'primaryFont',
                    selectedPrimaryFont,
                    selectedSecondaryFont,
                    selectedTertiaryFont,
                  })
                }
                options={primaryFonts}
                buttonOnClick={() => handleFontSelectionClick('primary')}
                disabled={!isPrimaryFontCustomizationEnabled || !handleFontChange}
                useNativeSelectOnMobile={false}
                optionsListClassname="dark"
              />
            </SelectContainer>
          </TooltipV2>
        )}
        {secondaryFonts?.length > 0 && (
          <TooltipV2
            value={i18n.t(`${TRANSLATION}.font_customization_warning`)}
            width={210}
            multiline={true}
            position="bottom"
            isActive={!isSecondaryFontCustomizationEnabled}
          >
            <SelectContainer>
              <SelectLabel>{i18n.t(`${TRANSLATION}.secondary_font`)}</SelectLabel>
              <CustomSelect
                selected={selectedSecondaryFont}
                onSelect={value =>
                  handleFontChange?.({
                    value,
                    newFontType: 'secondaryFont',
                    selectedPrimaryFont,
                    selectedSecondaryFont,
                    selectedTertiaryFont,
                  })
                }
                options={secondaryFonts}
                disabled={!isSecondaryFontCustomizationEnabled || !handleFontChange}
                buttonOnClick={() => handleFontSelectionClick('secondary')}
                useNativeSelectOnMobile={false}
              />
            </SelectContainer>
          </TooltipV2>
        )}
        {tertiaryFonts?.length > 0 && (
          <TooltipV2
            value={i18n.t(`${TRANSLATION}.font_customization_warning`)}
            width={210}
            multiline={true}
            position="bottom"
            isActive={!isTertiaryFontCustomizationEnabled}
          >
            <SelectContainer>
              <SelectLabel>{i18n.t(`${TRANSLATION}.tertiary_font`)}</SelectLabel>
              <CustomSelect
                selected={selectedTertiaryFont}
                onSelect={value =>
                  handleFontChange?.({
                    value,
                    newFontType: 'tertiaryFont',
                    selectedPrimaryFont,
                    selectedSecondaryFont,
                    selectedTertiaryFont,
                  })
                }
                options={tertiaryFonts}
                disabled={!isTertiaryFontCustomizationEnabled || !handleFontChange}
                buttonOnClick={() => handleFontSelectionClick('tertiary')}
                useNativeSelectOnMobile={false}
              />
            </SelectContainer>
          </TooltipV2>
        )}
      </FontContainer>

      <SliderContainer>
        <SliderLabel>{i18n.t(`${TRANSLATION}.spacing`)}</SliderLabel>
        <SpacingSlider resume={resume} handleSpacingChange={handleSpacingChange} />
      </SliderContainer>

      {currentTemplate?.supportsFontSizesCustomization ? (
        <GroupControl title={i18n.t(`${TRANSLATION}.font_sizes_label`)} spacing="L">
          <FontSizesCustomization
            resume={resume}
            handleSettingsFieldChange={props.handleSettingsFieldChange}
          />
        </GroupControl>
      ) : (
        <SliderContainer>
          <SliderLabel>{i18n.t(`${TRANSLATION}.size`)}</SliderLabel>
          <SizeSlider resume={resume} handleSizeChange={handleSizeChange} />
        </SliderContainer>
      )}

      {currentTemplate?.supportsFontWeightsCustomization && (
        <GroupControl title={i18n.t(`${TRANSLATION}.font_weights_label`)} spacing="L">
          <FontWeightsCustomization
            resume={resume}
            handleSettingsFieldChange={props.handleSettingsFieldChange}
          />
        </GroupControl>
      )}
    </TabContentContainer>
  )
}
